/**
 * The editor's tabs, and the seventh that is not one yet.
 *
 * RULE-080, from PRD §7.5 verbatim: "the **Voice** tab renders only when the
 * organisation holds the `voice_enabled` entitlement *and* voice is enabled on
 * the funnel. Most accounts therefore see six tabs."
 *
 * AT M1 THAT GATE IS NOT REACHED FROM EITHER SIDE, and this file does not
 * pretend otherwise. Voice is M5: nothing renders a persona, a knowledge base,
 * a cap or a disclosure, and no surface in the product sets `voice.enabled`.
 * Writing the two-condition gate now would be a branch nothing can take, tested
 * against a body that does not exist — a preemptive rule, which CLAUDE.md's
 * process section forbids for exactly the reason it would be one here.
 *
 * So the seventh tab is the disabled "Coming soon" placeholder, unconditionally
 * — the ONE placeholder DECISION-016 sanctions, because PRD §7.5 asks for it by
 * name. `tests/editor-tabs.test.ts` asserts that no combination of entitlement
 * and funnel flag enables it, which is RULE-080 as it applies today: most
 * accounts see six usable tabs, and at M1 every account does. M5's story is
 * where the gate becomes live, and it is the story that will have a body to
 * put behind it.
 *
 * Each tab names the story that fills it. Story 6.2 is the shell only.
 *
 * Story 6.2.
 */
export const TAB_IDS = ['build', 'logic', 'qualify', 'design', 'connect', 'results'] as const
export type TabId = (typeof TAB_IDS)[number]

export type Tab = {
  id: TabId | 'voice'
  label: string
  /** The story that replaces the placeholder. */
  story: string
  what: string
  disabled: boolean
  badge?: string
}

/** The six, in PRD §7.5's order — which is the order the work happens in. */
export const TABS: readonly Tab[] = [
  { id: 'build', label: 'Build', story: '6.3', what: 'Steps, blocks, and the inspector beside them.', disabled: false },
  { id: 'logic', label: 'Logic', story: '6.4', what: 'Branching rules, and which respondents each one reached.', disabled: false },
  { id: 'qualify', label: 'Qualify', story: '7.1', what: 'Criteria, disqualifiers, and the band ladder.', disabled: false },
  { id: 'design', label: 'Design', story: '6.5', what: 'Theme, layout mode, and a live preview beside them.', disabled: false },
  { id: 'connect', label: 'Connect', story: '9.1', what: 'Where a scored lead goes, per band — and the embed snippet (6.7).', disabled: false },
  { id: 'results', label: 'Results', story: '8.7', what: 'Started, finished, qualified, and the band distribution.', disabled: false },
]

/** The seventh. Present, disabled, and honest about why. */
export const VOICE_TAB: Tab = {
  id: 'voice',
  label: 'Voice',
  story: 'M5',
  what: 'Persona, knowledge base, caps and disclosure for the AI voice agent.',
  disabled: true,
  badge: 'Coming soon',
}

/** What the strip renders. Seven, one of them disabled. */
export const ALL_TABS: readonly Tab[] = [...TABS, VOICE_TAB]

/** Read a tab from a path segment, falling back to Build. */
export function tabFromPath(segment: string | undefined): TabId {
  return (TAB_IDS as readonly string[]).includes(segment ?? '') ? (segment as TabId) : 'build'
}
